import { useMutation } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'

// Types for file upload operations
export interface FileDetail {
  name: string
  contentType: string
  size?: number
}

export interface Endpoint {
  id: string
  uploadUrl: string
  downloadUrl?: string
  key: string
}

const URL = import.meta.env.VITE_EPC_API_URL ?? ''

/**
 * Hook for getting presigned URLs for file uploads
 */
export const usePresignedUrls = () => {
  const fetchWithAuth = useFetchWithAuth()

  return useMutation({
    mutationFn: async ({
      eventId,
      files,
    }: {
      eventId: string
      files: FileDetail[]
    }): Promise<Endpoint[]> => {
      try {
        const data = await fetchWithAuth(
          `${URL}/events/${eventId}/attachments/presigned-urls`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ files }),
          }
        )

        return data.endpoints || []
      } catch (error) {
        console.error('Failed to get presigned URLs:', error)
        throw new Error('Failed to get presigned URLs')
      }
    },
  })
}

/**
 * Hook for uploading a file to S3 or another storage service
 */
export const useS3Upload = () => {
  return useMutation({
    mutationFn: async ({
      url,
      file,
      contentType,
      onProgress,
    }: {
      url: string
      file: File
      contentType: string
      onProgress?: (progress: number) => void
    }) => {
      // If we're using XMLHttpRequest for progress tracking
      if (onProgress) {
        return new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest()

          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const percentComplete = Math.round(
                (event.loaded / event.total) * 100
              )
              onProgress(percentComplete)
            }
          })

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve()
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`))
            }
          })

          xhr.addEventListener('error', () => {
            reject(new Error('Upload failed due to network error'))
          })

          xhr.addEventListener('abort', () => {
            reject(new Error('Upload aborted'))
          })

          xhr.open('PUT', url, true)
          xhr.setRequestHeader('Content-Type', contentType)
          xhr.send(file)
        })
      } else {
        // If no progress tracking needed, use fetch
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': contentType,
          },
          body: file,
        })

        if (!response.ok) {
          throw new Error('Failed to upload file')
        }
      }
    },
  })
}

/**
 * Hook for completing file uploads (letting the server know uploads are done)
 */
export const useCompleteUploads = () => {
  const fetchWithAuth = useFetchWithAuth()

  return useMutation({
    mutationFn: async ({
      eventId,
      fileIds,
    }: {
      eventId: string
      fileIds: string[]
    }) => {
      try {
        const data = await fetchWithAuth(
          `${URL}/file-uploads/${eventId}/complete`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ file_ids: fileIds }),
          }
        )

        return data
      } catch (error) {
        console.error('Failed to complete file uploads:', error)
        throw new Error('Failed to complete file uploads')
      }
    },
  })
}
